import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { CheckCircle, LockSimple, LockSimpleOpen, FloppyDisk } from "@phosphor-icons/react";

const ADJ = ["l104", "l107", "l204", "l207"];

export function DeclarationForm({ report, onChange, isAdmin }) {
  const filed = report.status === "filed";
  const [adj, setAdj] = useState(report.adjustments || {});
  const [dirty, setDirty] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [conf, setConf] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAdj(report.adjustments || {}); setDirty(false); }, [report]);

  const L = report.lines;
  const preview = (() => {
    const a = Object.fromEntries(ADJ.map((k) => [k, Number(adj[k]) || 0]));
    const l105 = L.l103 + a.l104, l108 = L.l106 + a.l107, l205 = L.l203 + a.l204, l208 = L.l206 + a.l207;
    const tps = l105 - l108, tvq = l205 - l208;
    return { ...L, ...a, l105, l108, l205, l208, tps_net: tps, tvq_net: tvq, total: tps + tvq };
  })();

  const saveAdj = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/reports/tax/adjustments?month=${report.period}`, Object.fromEntries(ADJ.map((k) => [k, Number(adj[k]) || 0])));
      onChange(r.data); toast.success("Redressements enregistrés");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const file = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/reports/tax/file?month=${report.period}`, { confirmation_number: conf, notes });
      onChange(r.data); setFileOpen(false); toast.success("Déclaration marquée comme produite");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const unfile = async () => {
    if (!window.confirm("Rouvrir cette déclaration ? Les montants seront recalculés en direct.")) return;
    try { const r = await api.post(`/reports/tax/unfile?month=${report.period}`); onChange(r.data); toast.success("Déclaration rouverte"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const netCls = (v) => (v >= 0 ? "text-red-600" : "text-emerald-700");
  const row = ({ no, label, value, bold, editable, k }) => (
    <div className={`flex items-center justify-between gap-4 py-2 border-b border-border/60 ${bold ? "font-heading font-bold" : "text-sm"}`} data-testid={`decl-row-${k || no}`}>
      <span className={bold ? "" : "text-muted-foreground"}>{label}{no && <span className="ml-1 text-xs text-muted-foreground">(ligne {no})</span>}</span>
      {editable && !filed ? (
        <div className="flex items-center gap-1">
          <Input data-testid={`decl-input-${k}`} type="number" step="0.01" className="rounded-sm w-36 text-right tabular-nums h-8"
            value={adj[k] ?? 0} onChange={(e) => { setAdj({ ...adj, [k]: e.target.value }); setDirty(true); }} />
          <span className="text-muted-foreground text-sm">$</span>
        </div>
      ) : (
        <span className={`tabular-nums ${bold ? netCls(value) : ""}`}>{money(value)}</span>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="tax-declaration">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="font-heading text-lg font-bold">{report.period_label}</div>
          {filed ? (
            <Badge data-testid="decl-status" className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-700"><LockSimple size={12} className="mr-1" /> Produite le {String(report.filed_at).slice(0, 10)}</Badge>
          ) : (
            <Badge data-testid="decl-status" variant="outline" className="rounded-sm">Brouillon — calcul en direct</Badge>
          )}
          {report.confirmation_number && <span className="text-xs text-muted-foreground">No confirmation : <strong>{report.confirmation_number}</strong></span>}
        </div>
        <div className="flex gap-2">
          {!filed && <Button data-testid="decl-save-adj" variant="outline" className="rounded-sm" disabled={!dirty || busy} onClick={saveAdj}><FloppyDisk size={16} className="mr-1" /> Enregistrer les redressements</Button>}
          {!filed && <Button data-testid="decl-file-btn" className="rounded-sm" disabled={busy} onClick={() => setFileOpen(true)}><CheckCircle size={16} className="mr-1" /> Marquer comme produite</Button>}
          {filed && isAdmin && <Button data-testid="decl-unfile-btn" variant="outline" className="rounded-sm" onClick={unfile}><LockSimpleOpen size={16} className="mr-1" /> Rouvrir</Button>}
        </div>
      </div>
      <div className="text-sm text-muted-foreground">{report.invoices_count} facture(s) · {report.expenses_count} dépense(s) · Ventes taxables {money(report.sales_subtotal)} · Dépenses {money(report.expense_subtotal)}</div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-heading font-bold uppercase tracking-wide text-sm mb-3">Déclaration de la TPS/TVH</h3>
          {row({ no: "103", label: "TPS/TVH exigible", value: preview.l103 })}
          {row({ no: "104", label: "Redressements de la TPS/TVH", value: preview.l104, editable: true, k: "l104" })}
          {row({ no: "105", label: "Total de la TPS/TVH exigible et des redressements", value: preview.l105 })}
          {row({ no: "106", label: "Crédits de taxe sur les intrants (CTI)", value: preview.l106 })}
          {row({ no: "107", label: "Redressements de CTI et autres redressements", value: preview.l107, editable: true, k: "l107" })}
          {row({ no: "108", label: "Total des CTI et des redressements", value: preview.l108 })}
          {row({ label: "Total de la TPS/TVH à remettre ou remboursement", value: preview.tps_net, bold: true, k: "tps-net" })}
        </div>
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-heading font-bold uppercase tracking-wide text-sm mb-3">Déclaration de la TVQ</h3>
          {row({ no: "203", label: "TVQ exigible", value: preview.l203 })}
          {row({ no: "204", label: "Redressements de la TVQ", value: preview.l204, editable: true, k: "l204" })}
          {row({ no: "205", label: "Total de la TVQ exigible et des redressements", value: preview.l205 })}
          {row({ no: "206", label: "Remboursement de la taxe sur les intrants (RTI)", value: preview.l206 })}
          {row({ no: "207", label: "Redressements de RTI et autres redressements", value: preview.l207, editable: true, k: "l207" })}
          {row({ no: "208", label: "Total des RTI et des redressements", value: preview.l208 })}
          {row({ label: "Total de la TVQ à remettre ou remboursement", value: preview.tvq_net, bold: true, k: "tvq-net" })}
        </div>
      </div>
      <div className="border border-foreground bg-card rounded-sm p-6 flex items-center justify-between">
        <div>
          <div className="font-heading font-bold uppercase tracking-wide text-sm">Total de la déclaration</div>
          <div className="text-xs text-muted-foreground">{preview.total >= 0 ? "Montant à remettre" : "Remboursement attendu"}</div>
        </div>
        <span data-testid="net-total" className={`font-heading text-2xl font-bold tabular-nums ${netCls(preview.total)}`}>{money(preview.total)}</span>
      </div>
      {report.notes && <p className="text-sm text-muted-foreground" data-testid="decl-notes">Notes : {report.notes}</p>}

      <Dialog open={fileOpen} onOpenChange={setFileOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">Marquer la déclaration comme produite</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Les montants de {report.period_label} seront figés tels qu'affichés ({money(preview.total)}). Enregistrez d'abord vos redressements si vous en avez modifié.</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>No de confirmation Revenu Québec (optionnel)</Label><Input data-testid="decl-conf-input" className="rounded-sm" value={conf} onChange={(e) => setConf(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea data-testid="decl-notes-input" className="rounded-sm" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setFileOpen(false)}>Annuler</Button>
            <Button data-testid="decl-file-confirm" className="rounded-sm" disabled={busy || dirty} onClick={file}>Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DeclarationHistory({ current, onSelect, refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/tax/history").then((r) => setRows(r.data)).catch(() => setRows([])); }, [refreshKey]);
  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="decl-history">
      <div className="px-6 py-4 border-b border-border"><h2 className="font-heading font-bold">Historique des déclarations</h2></div>
      <Table>
        <TableHeader><TableRow className="bg-muted/50">
          <TableHead>Période</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">TPS nette</TableHead>
          <TableHead className="text-right">TVQ nette</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Produite le</TableHead><TableHead>No confirmation</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucune période.</TableCell></TableRow>}
          {rows.map((r) => (
            <TableRow key={r.period} data-testid={`decl-history-${r.period}`} className={`cursor-pointer ${r.period === current ? "bg-primary/10" : ""}`} onClick={() => onSelect(r.period)}>
              <TableCell className="font-medium">{r.period_label}</TableCell>
              <TableCell>{r.status === "filed" ? <Badge className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-700">Produite</Badge> : <Badge variant="outline" className="rounded-sm">À produire</Badge>}</TableCell>
              <TableCell className="text-right tabular-nums">{money(r.tps_net)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(r.tvq_net)}</TableCell>
              <TableCell className={`text-right tabular-nums font-medium ${r.total >= 0 ? "text-red-600" : "text-emerald-700"}`}>{money(r.total)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.filed_at ? String(r.filed_at).slice(0, 10) : "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.confirmation_number || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
